/**
 * Why the rehearsal banner is up (#139).
 *
 * The page held `bannerOpen: boolean` and rendered one fixed title and
 * body for every failure. A visitor who had used the demo's free credits
 * read "Stage unreachable — check your connection and try again", which
 * blames their network for a server-side ceiling and offers a retry that
 * can only fail the same way.
 *
 * A boolean cannot carry a reason. That is the whole of the bug, and it
 * is the same shape as an `isActive` column nothing reconciles: the state
 * is real, and the thing rendered from it is not what a reader thinks.
 *
 * The classification lives here rather than inline in the page so it can
 * be tested against a constructed `ApiError` without mounting anything.
 */

import { isApiError } from "@/api";
import { ERROR_CODE_DEMO_AT_CAPACITY } from "@/voice/costCap";

/** Keys of `rehearsalStageCopy.banner`. */
export type BannerReason =
  | "unreachable"
  | "session-credits"
  | "demo-at-capacity"
  | "voice-limit";

/**
 * Classify a session-creation failure.
 *
 * Mirrors `handleCapResponse` in `src/voice/costCap.ts`, which does the
 * same job for the voice paths: the server returns 429 for both the
 * per-visitor ceiling and the global one, so the status alone cannot tell
 * them apart and the body's error code is what separates them.
 *
 * Anything that is not an `ApiError` — a dropped connection, a DNS
 * failure, a parse error — is genuinely unreachable, and so is a 5xx:
 * the stage did not open and trying again is a reasonable thing to do.
 */
export function bannerReasonFor(error: unknown): BannerReason {
  if (!isApiError(error)) return "unreachable";

  if (error.status === 429) {
    return error.code === ERROR_CODE_DEMO_AT_CAPACITY
      ? "demo-at-capacity"
      : "session-credits";
  }
  if (error.status === 402) return "voice-limit";

  return "unreachable";
}
